import { InformationCircleIcon } from '@heroicons/react/24/outline';
import Tooltip from 'antd/es/tooltip';

import { ErrorableDetailsItem } from '@pages/Payouts/Details/ErrorableDetailsItem';
import type { PayoutDraft } from 'api';

import { DetailsDraftAttachmentSectionContainer } from '@atoms/DetailsAttachmentSection/DetailsDraftAttachmentSectionContainer';
import { FormattedDate } from '@atoms/FormattedDate/FormattedDate';

import { CopyToClipboardButton } from '@molecules/CopyToClipboardButton';
import { Country } from '@molecules/Country';

import { PAYOUT_DRAFTS_DETAILS_HEADER_PAYOUT_REQUEST_TEST_ID } from '../constants';

import type { CollapseItemWithTypedSection } from '.';

export const getCollapseItems = (
  payoutDraftData: PayoutDraft | undefined,
  isSuccess: boolean
): CollapseItemWithTypedSection[] => {
  if (!payoutDraftData || !isSuccess) {
    return [];
  }

  const sections: CollapseItemWithTypedSection[] = [];

  sections.push(
    {
      key: 'payout',
      label: (
        <div
          className="flex gap-2"
          data-testid={PAYOUT_DRAFTS_DETAILS_HEADER_PAYOUT_REQUEST_TEST_ID}
        >
          Payout Request
        </div>
      ),
      children: (
        <div>
          <ErrorableDetailsItem
            title="End-to-end ID"
            item={
              <CopyToClipboardButton
                text={payoutDraftData?.end_to_end_id?.toLocaleUpperCase()}
              />
            }
            data-testid="payout-draft-details-e2e-id"
          />
          <ErrorableDetailsItem
            title="Amount"
            item={
              <div>
                <div>{payoutDraftData?.amount}</div>
                <div className="text-xs font-normal">
                  {payoutDraftData?.currency_code}
                </div>
              </div>
            }
            data-testid="payout-draft-details-amount"
          />
          <ErrorableDetailsItem
            title="Country"
            item={
              <Country countryIso={payoutDraftData?.creditor?.country_code} />
            }
            data-testid="payout-draft-details-creditor-destination-country"
          />
          <ErrorableDetailsItem
            title="Selected Product"
            item={payoutDraftData?.local_instrument}
            data-testid="payout-draft-details-selected-product"
          />
          {payoutDraftData?.debtor?.website && (
            <ErrorableDetailsItem
              title="Website"
              item={payoutDraftData.debtor.website}
              data-testid="payout-draft-details-debtor-website"
            />
          )}
        </div>
      )
    },
    {
      key: 'sender',
      label: 'Sender',
      children: (
        <div>
          <ErrorableDetailsItem
            title="Name"
            item={payoutDraftData?.ultimate_debtor?.name}
            data-testid="payout-draft-details-ultimate-debtor-name"
          />
          <ErrorableDetailsItem
            title="Date of Birth"
            item={
              <FormattedDate
                value={payoutDraftData?.ultimate_debtor?.birthdate}
                className="text-base font-medium"
              />
            }
            data-testid="payout-draft-details-ultimate-debtor-dob"
          />
          <ErrorableDetailsItem
            title="Address lines"
            item={payoutDraftData?.ultimate_debtor?.address_lines}
            data-testid="payout-draft-details-ultimate-debtor-address-lines"
          />
          {payoutDraftData?.ultimate_debtor?.city && (
            <ErrorableDetailsItem
              title="City"
              item={payoutDraftData?.ultimate_debtor?.city}
              data-testid="payout-draft-details-ultimate-debtor-city"
            />
          )}
          {payoutDraftData?.ultimate_debtor?.country_code && (
            <ErrorableDetailsItem
              title="Country"
              item={
                <Country
                  countryIso={payoutDraftData?.ultimate_debtor?.country_code}
                />
              }
              data-testid="payout-draft-details-ultimate-debtor-country"
            />
          )}
          {payoutDraftData?.ultimate_debtor?.type && (
            <ErrorableDetailsItem
              title="Type"
              item={payoutDraftData?.ultimate_debtor?.type}
              data-testid="payout-draft-details-ultimate-debtor-type"
            />
          )}
        </div>
      )
    },
    {
      key: 'receiver_bank',
      label: 'Receiver Bank',
      children: (
        <div>
          {payoutDraftData?.creditor_account?.iban && (
            <ErrorableDetailsItem
              title="IBAN"
              item={
                <CopyToClipboardButton
                  text={payoutDraftData?.creditor_account?.iban}
                />
              }
              data-testid="payout-draft-details-creditor-receiver-bank"
            />
          )}
          {payoutDraftData?.creditor_account?.country_code && (
            <ErrorableDetailsItem
              title="Country"
              item={
                <Country
                  countryIso={payoutDraftData?.creditor_account?.country_code}
                />
              }
              data-testid="payout-draft-details-creditor-country-code"
            />
          )}
          {payoutDraftData?.creditor_account?.account_number && (
            <ErrorableDetailsItem
              title="Account number"
              item={payoutDraftData?.creditor_account?.account_number}
              data-testid="payout-draft-details-creditor-account-number"
            />
          )}
          {payoutDraftData?.creditor_account?.bank_clearing_system_code && (
            <ErrorableDetailsItem
              title="Clearing system code"
              item={
                payoutDraftData?.creditor_account?.bank_clearing_system_code
              }
              data-testid="payout-draft-details-creditor-clearing-system-code"
            />
          )}
          {payoutDraftData?.creditor_account?.bank_bic && (
            <ErrorableDetailsItem
              title="BIC"
              item={payoutDraftData?.creditor_account?.bank_bic}
              data-testid="payout-draft-details-creditor-bic"
            />
          )}
          {payoutDraftData?.creditor_account?.bank_branch_id && (
            <ErrorableDetailsItem
              title="Branch ID"
              item={payoutDraftData?.creditor_account?.bank_branch_id}
              data-testid="payout-draft-details-creditor-branch-id"
            />
          )}
        </div>
      )
    },
    {
      key: 'receiver',
      label: 'Receiver',
      children: (
        <div>
          {payoutDraftData?.creditor?.name && (
            <ErrorableDetailsItem
              title="Name"
              item={payoutDraftData?.creditor?.name}
              data-testid="payout-draft-details-creditor-name"
            />
          )}
          {payoutDraftData?.creditor?.birthdate && (
            <ErrorableDetailsItem
              title="Date of Birth"
              item={
                <FormattedDate
                  value={payoutDraftData?.creditor?.birthdate}
                  className="font-semibold"
                />
              }
              data-testid="payout-draft-details-creditor-dob"
            />
          )}
          {payoutDraftData?.creditor?.city && (
            <ErrorableDetailsItem
              title="City"
              item={payoutDraftData?.creditor?.city}
              data-testid="payout-draft-details-creditor-city"
            />
          )}
          {payoutDraftData?.creditor?.country_code && (
            <ErrorableDetailsItem
              title="Country"
              item={
                <Country countryIso={payoutDraftData?.creditor?.country_code} />
              }
              data-testid="payout-draft-details-creditor-country-code"
            />
          )}
        </div>
      )
    },
    {
      key: 'attachments',
      label: (
        <div className="flex items-center gap-1">
          Attachments
          <Tooltip
            title="This feature is not intended for RFIs"
            placement="right"
            arrow={true}
          >
            <InformationCircleIcon className="size-4 stroke-current" />
          </Tooltip>
        </div>
      ),
      children: (
        <DetailsDraftAttachmentSectionContainer
          resourceId={payoutDraftData.id}
        />
      )
    }
  );

  // Add Intermediary Agent info if required
  if (
    payoutDraftData?.intermediary_agent?.bank_bic &&
    payoutDraftData?.intermediary_agent?.bank_name &&
    payoutDraftData?.intermediary_agent_account?.account_number &&
    payoutDraftData?.intermediary_agent_account?.iban
  ) {
    sections.push(
      {
        key: 'intermediary_agent',
        label: 'Intermediary Agent',
        children: (
          <div>
            {payoutDraftData?.intermediary_agent?.bank_bic && (
              <ErrorableDetailsItem
                title="Bank bic"
                item={payoutDraftData?.intermediary_agent?.bank_bic}
                data-testid="payout-details-intermediary-agent-bank-bic"
              />
            )}
            {payoutDraftData?.intermediary_agent?.bank_name && (
              <ErrorableDetailsItem
                title="Bank name"
                item={payoutDraftData?.intermediary_agent?.bank_name}
                data-testid="payout-details-intermediary-agent-bank-name"
              />
            )}
          </div>
        )
      },
      {
        key: 'intermediary_agent_account',
        label: 'Intermediary Agent Account',
        children: (
          <div>
            {payoutDraftData?.intermediary_agent_account?.account_number && (
              <ErrorableDetailsItem
                title="Account number"
                item={
                  payoutDraftData?.intermediary_agent_account?.account_number
                }
                data-testid="payout-details-intermediary-agent-account"
              />
            )}
            {payoutDraftData?.intermediary_agent_account?.iban && (
              <ErrorableDetailsItem
                title="IBAN"
                item={payoutDraftData?.intermediary_agent_account?.iban}
                data-testid="payout-details-intermediary-agent-account-iban"
              />
            )}
          </div>
        )
      }
    );
  }

  return sections;
};
